import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataIngestionStatus = () => {
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const dataStreams = [
    {
      id: 'platform_signals',
      name: 'Platform Signals',
      description: 'Real-time feeds from major social platforms',
      status: 'active',
      health: 'healthy',
      lastIngestion: new Date(Date.now() - 120000),
      itemsProcessed: 15420,
      itemsPerHour: 8760,
      sources: ['Facebook', 'Twitter', 'TikTok', 'Instagram', 'YouTube'],
      latency: 45,
      errorRate: 0.02,
      dataQuality: 0.94
    },
    {
      id: 'edmo_hubs',
      name: 'EDMO Hubs',
      description: 'European Digital Media Observatory network',
      status: 'active',
      health: 'healthy',
      lastIngestion: new Date(Date.now() - 300000),
      itemsProcessed: 3240,
      itemsPerHour: 1890,
      sources: ['EDMO Germany', 'EDMO France', 'EDMO Italy', 'EDMO Spain'],
      latency: 120,
      errorRate: 0.01,
      dataQuality: 0.97
    },
    {
      id: 'osint_sources',
      name: 'OSINT Sources',
      description: 'Open source intelligence feeds',
      status: 'active',
      health: 'warning',
      lastIngestion: new Date(Date.now() - 600000),
      itemsProcessed: 8760,
      itemsPerHour: 4320,
      sources: ['News APIs', 'Forum Scrapers', 'Telegram Monitors', 'Reddit Feeds'],
      latency: 180,
      errorRate: 0.08,
      dataQuality: 0.89
    },
    {
      id: 'institutional_feeds',
      name: 'Institutional Feeds',
      description: 'Government and official sources',
      status: 'active',
      health: 'healthy',
      lastIngestion: new Date(Date.now() - 900000),
      itemsProcessed: 1560,
      itemsPerHour: 720,
      sources: ['EU Commission', 'Member State APIs', 'Official Channels'],
      latency: 300,
      errorRate: 0.00,
      dataQuality: 0.99
    },
    {
      id: 'research_partners',
      name: 'Research Partners',
      description: 'Academic and research institution data',
      status: 'maintenance',
      health: 'offline',
      lastIngestion: new Date(Date.now() - 3600000),
      itemsProcessed: 0,
      itemsPerHour: 0,
      sources: ['University Networks', 'Research Labs', 'Think Tanks'],
      latency: 0,
      errorRate: 0.00,
      dataQuality: 0.95
    }
  ];

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'offline': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getHealthIcon = (health) => {
    switch (health) {
      case 'healthy': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'error': return 'XCircle';
      case 'offline': return 'Circle';
      default: return 'Circle';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'Play';
      case 'paused': return 'Pause';
      case 'maintenance': return 'Settings';
      case 'error': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const totalItemsProcessed = dataStreams?.reduce((sum, stream) => sum + stream?.itemsProcessed, 0);
  const totalItemsPerHour = dataStreams?.reduce((sum, stream) => sum + stream?.itemsPerHour, 0);
  const averageLatency = dataStreams?.reduce((sum, stream) => sum + stream?.latency, 0) / dataStreams?.length;
  const averageErrorRate = dataStreams?.reduce((sum, stream) => sum + stream?.errorRate, 0) / dataStreams?.length;

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Database" size={20} className="text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Data Ingestion Status</h3>
              <p className="text-sm text-muted-foreground">Real-time monitoring of data streams and processing</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e?.target?.value))}
              className="px-3 py-2 bg-input border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value={10}>10s refresh</option>
              <option value={30}>30s refresh</option>
              <option value={60}>1m refresh</option>
              <option value={300}>5m refresh</option>
            </select>
            <Button variant="outline" size="sm" iconName="RefreshCw">
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Activity" size={16} className="text-primary" />
              <span className="text-xs text-muted-foreground">Total Processed</span>
            </div>
            <div className="text-lg font-semibold text-foreground mt-1">
              {totalItemsProcessed?.toLocaleString()}
            </div>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="TrendingUp" size={16} className="text-green-500" />
              <span className="text-xs text-muted-foreground">Items/Hour</span>
            </div>
            <div className="text-lg font-semibold text-foreground mt-1">
              {totalItemsPerHour?.toLocaleString()}
            </div>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-orange-500" />
              <span className="text-xs text-muted-foreground">Avg Latency</span>
            </div>
            <div className="text-lg font-semibold text-foreground mt-1">
              {averageLatency?.toFixed(0)}ms
            </div>
          </div>
          <div className="p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-red-500" />
              <span className="text-xs text-muted-foreground">Error Rate</span>
            </div>
            <div className="text-lg font-semibold text-foreground mt-1">
              {(averageErrorRate * 100)?.toFixed(2)}%
            </div>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-foreground">Data Streams ({dataStreams?.length})</h4>
          <span className="text-xs text-muted-foreground">
            Last updated: {formatTimeAgo(lastUpdate)}
          </span>
        </div>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {dataStreams?.map((stream) => (
            <div
              key={stream?.id}
              className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-smooth"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name={getStatusIcon(stream?.status)} size={16} className="text-primary" />
                    <h5 className="text-sm font-semibold text-foreground">{stream?.name}</h5>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getHealthColor(stream?.health)}`}>
                      <Icon name={getHealthIcon(stream?.health)} size={12} className="inline mr-1" />
                      {stream?.health}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{stream?.description}</p>
                  <div className="text-xs text-muted-foreground">
                    Last ingestion: {formatTimeAgo(stream?.lastIngestion)}
                  </div>
                </div>
                <Button variant="ghost" size="icon" iconName="Settings" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <div className="text-xs text-muted-foreground">Processed</div>
                  <div className="text-sm font-medium text-foreground">{stream?.itemsProcessed?.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Per Hour</div>
                  <div className="text-sm font-medium text-foreground">{stream?.itemsPerHour?.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Latency</div>
                  <div className="text-sm font-medium text-foreground">{stream?.latency}ms</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Quality</div>
                  <div className="text-sm font-medium text-foreground">{(stream?.dataQuality * 100)?.toFixed(0)}%</div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-1">
                  {stream?.sources?.slice(0, 3)?.map((source, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded-full"
                    >
                      {source}
                    </span>
                  ))}
                  {stream?.sources?.length > 3 && (
                    <span className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded-full">
                      +{stream?.sources?.length - 3} more
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {stream?.status === 'active' ? (
                    <Button variant="outline" size="sm" iconName="Pause">
                      Pause
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" iconName="Play">
                      Resume
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" iconName="BarChart3">
                    Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DataIngestionStatus;